import React, { useState, useEffect } from 'react'
import { globalState } from '../../../store'

import styled from 'styled-components'
import { useParams } from 'react-router-dom'
import { usePost } from '../../../components/component-items/helpers'
import { OrderInfo } from './styles'

// Components
import GlobalStyles from '../../../components/component-items/styles'
import Checkbox from '../../../components/component-items/checkbox'
import { Form, InputGroup } from 'react-bootstrap'
import { PermissionGate } from '../../../components/rbac'

import { ProductBarcodeIdentifierType, ProductBarcodeIdentifierTypeOptions } from '../../../enums'
import { ProductDetailsProps } from './types'

const Label = styled(Form.Label)`
  margin-top: 1em;
`

const SectionHeader = styled.h5`
  margin-top: 1.5em;
  margin-bottom: 0.5em;
  padding-bottom: 0.5em;
  border-bottom: 1px solid #e0e0e0;
  color: #666;
  font-size: 0.9em;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

export const ProductDetails = ({ productDetails }: ProductDetailsProps) => {
  const productVariant = useParams<{ id: string }>().id
  const url = `/api/product/${productVariant}/details/`
  const {
    state: { csrf },
  } = globalState()

  const [product, setProduct] = useState<any>({ barcode_identifier_type: ProductBarcodeIdentifierType.UPC })

  useEffect(() => {
    setProduct({ barcode_identifier_type: ProductBarcodeIdentifierType.UPC, ...productDetails })
  }, [productDetails])

  return (
    <OrderInfo style={{ display: 'block', height: 'unset' }}>
      <GlobalStyles.CardHeader style={{ padding: '0 12px' }}>
        <GlobalStyles.CardTitle>
          <h3>{product.is_packaging ? 'Packaging' : 'Product'} Details</h3>
        </GlobalStyles.CardTitle>
        <GlobalStyles.CardToolbar>
          <GlobalStyles.Button onClick={() => usePost(url, { ...product }, csrf)}>Update</GlobalStyles.Button>
        </GlobalStyles.CardToolbar>
      </GlobalStyles.CardHeader>
      <div style={{ minHeight: 500, width: '100%' }}>
        <Form.Group style={{ margin: '1em' }}>
          <Label>Title</Label>
          <Form.Control
            type="text"
            value={product.title || ''}
            onChange={(e: any) => setProduct({ ...product, title: e.target.value })}
          />
          <Label>Description</Label>
          <Form.Control
            as="textarea"
            aria-label="Product Description"
            value={product.description}
            onChange={(e: any) => setProduct({ ...product, description: e.target.value })}
          />
          <Label>
            <GlobalStyles.Tooltip
              data-title="Barcode identification scheme for this product"
              style={{ borderBottom: 'dashed 1px' }}
            >
              Barcode Type
            </GlobalStyles.Tooltip>
          </Label>
          <Form.Select
            value={product.barcode_identifier_type || ProductBarcodeIdentifierType.UPC}
            onChange={(e: any) => setProduct({ ...product, barcode_identifier_type: e.target.value })}
          >
            {(product.barcode_identifier_type_choices || ProductBarcodeIdentifierTypeOptions).map((option: any) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Form.Select>
          <PermissionGate segment="products" minLevel="admin">
            <>
              <Label>Inventory Control</Label>
              <Form.Select
                value={product.inventoryControl || 'fifo'}
                onChange={(e: any) => setProduct({ ...product, inventoryControl: e.target.value })}
              >
                <option value={'fifo'}>Lot Control - FIFO</option>
                <option value={'lifo'}>Lot Control - LIFO</option>
              </Form.Select>
              <Label>
                <GlobalStyles.Tooltip
                  data-title="Inventory will be ordered by this field"
                  style={{ borderBottom: 'dashed 1px' }}
                >
                  Inventory Control Field
                </GlobalStyles.Tooltip>
              </Label>
              <Form.Select
                value={product.inventory_control_category || 'received_date'}
                onChange={(e: any) => setProduct({ ...product, inventory_control_category: e.target.value })}
              >
                <option value={'lot_number'}>Lot Number</option>
                <option value={'received_date'}>Received Date</option>
                <option value={'manufacture_date'}>Manufacture Date</option>
                <option value={'best_before_date'}>Best Before Date</option>
                <option value={'expiry_date'}>Expiry Date</option>
              </Form.Select>
            </>
          </PermissionGate>
          <InputGroup style={{ marginTop: '2em' }}>
            <Checkbox selected={product.promo} setSelected={() => setProduct({ ...product, promo: !product.promo })} />
            <p style={{ padding: '0 1em', lineHeight: '24px' }}>Promotional Item</p>
          </InputGroup>
          <InputGroup>
            <Checkbox
              selected={product.media_mail}
              setSelected={() => setProduct({ ...product, media_mail: !product.media_mail })}
            />
            <p style={{ padding: '0 1em', lineHeight: '24px' }}>Eligible for Media Mail</p>
          </InputGroup>
          <InputGroup>
            <Checkbox
              selected={product.scan_serial_number}
              setSelected={() => setProduct({ ...product, scan_serial_number: !product.scan_serial_number })}
            />
            <p style={{ padding: '0 1em', lineHeight: '24px' }}>Product Requires Scanning Serial Numbers</p>
          </InputGroup>
          <SectionHeader>Freight Classification (LTL/TL)</SectionHeader>
          <Label>
            <GlobalStyles.Tooltip
              data-title="National Motor Freight Classification code for LTL shipping"
              style={{ borderBottom: 'dashed 1px' }}
            >
              NMFC Code
            </GlobalStyles.Tooltip>
          </Label>
          <Form.Control
            type="text"
            placeholder="e.g., 123456-01"
            value={product.nmfc_code || ''}
            onChange={(e: any) => setProduct({ ...product, nmfc_code: e.target.value })}
          />
          <Label>NMFC Description</Label>
          <Form.Control
            type="text"
            placeholder="e.g., Machinery, NOI"
            value={product.nmfc_description || ''}
            onChange={(e: any) => setProduct({ ...product, nmfc_description: e.target.value })}
          />
          <Label>
            <GlobalStyles.Tooltip
              data-title="Freight class determines shipping rates for LTL handling units (50-500)"
              style={{ borderBottom: 'dashed 1px' }}
            >
              Freight Class
            </GlobalStyles.Tooltip>
          </Label>
          <Form.Select
            value={product.freight_class || ''}
            onChange={(e: any) => setProduct({ ...product, freight_class: e.target.value })}
          >
            <option value="">-- Select Freight Class --</option>
            <option value="50">Class 50</option>
            <option value="55">Class 55</option>
            <option value="60">Class 60</option>
            <option value="65">Class 65</option>
            <option value="70">Class 70</option>
            <option value="77.5">Class 77.5</option>
            <option value="85">Class 85</option>
            <option value="92.5">Class 92.5</option>
            <option value="100">Class 100</option>
            <option value="110">Class 110</option>
            <option value="125">Class 125</option>
            <option value="150">Class 150</option>
            <option value="175">Class 175</option>
            <option value="200">Class 200</option>
            <option value="250">Class 250</option>
            <option value="300">Class 300</option>
            <option value="400">Class 400</option>
            <option value="500">Class 500</option>
          </Form.Select>
        </Form.Group>
      </div>
    </OrderInfo>
  )
}
